import { env } from "@/lib/env";

export interface DemoUser {
  email: string;
  password: string;
  name: string;
  role: string;
  description: string;
  seedType: "standard" | "german-demo";
  category: "admin" | "owner" | "worker" | "manager";
  isVip?: boolean;
}

// Standard demo users from seed.ts
export const standardDemoUsers: DemoUser[] = [
  {
    email: "admin@demo.com",
    password: "demo123",
    name: "Admin User",
    role: "admin",
    description: "Access to all salons in franchise",
    seedType: "standard",
    category: "admin",
    isVip: true,
  },
  {
    email: "owner.single@demo.com",
    password: "demo123",
    name: "Sarah Johnson",
    role: "single-owner",
    description: "Owner of one salon",
    seedType: "standard",
    category: "owner",
  },
  {
    email: "worker.single@demo.com",
    password: "demo123",
    name: "Mike Davis",
    role: "single-worker",
    description: "Worker at one salon",
    seedType: "standard",
    category: "worker",
  },
  {
    email: "owner.multi@demo.com",
    password: "demo123",
    name: "Lisa Chen",
    role: "multi-owner",
    description: "Owner of multiple salons",
    seedType: "standard",
    category: "owner",
  },
  {
    email: "worker.multi@demo.com",
    password: "demo123",
    name: "Alex Rivera",
    role: "multi-worker",
    description: "Worker at multiple salons",
    seedType: "standard",
    category: "worker",
  },
  {
    email: "admin.cross@demo.com",
    password: "demo123",
    name: "Diana Cross",
    role: "cross-franchise-admin",
    description: "Admin access to both franchises",
    seedType: "standard",
    category: "admin",
  },
  {
    email: "worker.santamonica@demo.com",
    password: "demo123",
    name: "Carlos Mendez",
    role: "single-worker",
    description: "Worker at Santa Monica location only",
    seedType: "standard",
    category: "worker",
  },
  {
    email: "owner.luxe@demo.com",
    password: "demo123",
    name: "Maya Patel",
    role: "single-owner",
    description: "Owner of single Luxe wellness studio",
    seedType: "standard",
    category: "owner",
  },
];

// German-Vietnamese demo users from seed-german-demo.ts
export const germanVietnameseDemoUsers: DemoUser[] = [
  {
    email: "owner.vietnamese@demo.com",
    password: "demo123",
    name: "Linh Nguyen",
    role: "franchise-owner",
    description: "Vietnamese owner in Germany - Saigon Beauty Lounge",
    seedType: "german-demo",
    category: "owner",
    isVip: true,
  },
  {
    email: "manager.vietnamese@demo.com",
    password: "demo123",
    name: "Mai Tran",
    role: "salon-manager",
    description: "Vietnamese manager - Berlin Mitte location",
    seedType: "german-demo",
    category: "manager",
  },
  {
    email: "worker.bilingual@demo.com",
    password: "demo123",
    name: "Anna Pham",
    role: "bilingual-worker",
    description: "Vietnamese-German worker - EUR pricing, VI interface",
    seedType: "german-demo",
    category: "worker",
  },
];

export interface SeedSection {
  title: string;
  description: string;
  users: DemoUser[];
  color: "default" | "blue" | "green" | "orange";
}

export function getDemoUsersSections(t: (key: string) => string): SeedSection[] {
  const sections: SeedSection[] = [];

  if (env.demoSeed === "standard" || env.demoSeed === "both") {
    sections.push({
      title: t("demoSections.standard.title"),
      description: t("demoSections.standard.description"),
      users: standardDemoUsers,
      color: "default",
    });
  }

  if (env.demoSeed === "german-demo" || env.demoSeed === "both") {
    sections.push({
      title: t("demoSections.german-demo.title"),
      description: t("demoSections.german-demo.description"),
      users: germanVietnameseDemoUsers,
      color: "blue",
    });
  }

  return sections;
}

export function getAllDemoUsers(t: (key: string) => string): DemoUser[] {
  const sections = getDemoUsersSections(t);
  return sections.flatMap((section) => section.users);
}

export function getDemoUserByEmail(
  email: string,
  t: (key: string) => string
): DemoUser | undefined {
  return getAllDemoUsers(t).find((user) => user.email === email);
}
